import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AppResource from 'general/constants/AppResource';
import AppRoute from 'general/constants/AppRoute';
import Global from 'general/utils/Global';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import DialogChargeInventoryAddView from '../DialogChargeInventoryAddView';
import PropTypes from 'prop-types';
import DialogInventoryAddView from '../DialogInventoryAddNew';

ChargeInventoryListView.propTypes = {
    stationId: PropTypes.string,
    showFullUI: PropTypes.bool,
};

const defaultColumnIds = [
    'IMAGE',
    'CODE',
    'NAME',
    'BRAND',
    'PRICE',
    'QUANTITY',
    'UNIT',
    'DESCRIPTION',
];

function ChargeInventoryListView({ stationId, chargeStationDetail = {}, showFullUI = true }) {
    // MARK: ---- PROPERTIES ----
    const router = useRouter();
    const { user } = useSelector((state) => state.auth);
    const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
    const prefKey = useMemo(() => {
        const userId = user.userId;
        const pathName = router.pathname;
        const key = `${userId}_${pathName}`;
        return key;
    }, [user, router]);
    const isMine = useMemo(() => {
        return chargeStationDetail?.agencyId === user?.agencyId || chargeStationDetail?.landlordId === user?.agencyId;
    }, [chargeStationDetail])
    const [filter, setFilter] = useState(Global.g_filterChargeInventoryList);
    const [chargeInventoryList, setChargeInventoryList] = useState([]);
    const [total, setTotal] = useState(0);
    useEffect(() => {
        let params = _.pickBy(filter, (value) => value !== null && value !== undefined && value !== '');
        if (!stationId) {
            if (user?.role === ChargeConstants.UserRole.AGENCY && Utils.isValid(user?.agencyId)) {
                params.agencyId = user?.agencyId;
            }
            ChargeHelper.API.requestGetChargeInventoryCategoryList(params)
                .then((response) => {
                    setChargeInventoryList(response?.categories);
                    setTotal(response?.total);
                })
                .finally(() => { });
        } else {
            params.stationId = stationId;
            if (user?.role === ChargeConstants.UserRole.AGENCY && Utils.isValid(user?.agencyId)) {
                params.agencyId = user?.agencyId;
            }
            ChargeHelper.API.requestGetChargeInventoryList(params)
                .then((response) => {
                    setChargeInventoryList(response?.items);
                    setTotal(response?.total);
                })
                .finally(() => { });
        }
    }, [filter, user, stationId]);
    const fullColumns = useMemo(() => {
        return [
            {
                id: 'IMAGE',
                name: UIHelper.buildTableColumn('Ảnh'),
                selector: (row) =>
                    UIHelper.buildAvatar(
                        row?.images ? Utils.getFullUrl(row?.images) : null,
                        40,
                        AppResource.icons.avatar.default,
                        false,
                        false
                    ),
                width: '70px',
            },
            {
                id: 'CODE',
                name: UIHelper.buildTableColumn('Mã sản phẩm'),
                selector: (row) => !stationId
                    ? row?.categoryCode ?? `SP${row?.categoryId?.toString().padStart(8, '0')}`
                    : row?.inventoryCode ?? `SP${row?.inventoryId?.toString().padStart(8, '0')}`,
                minWidth: '160px',
                style: tableStyle.columns.link,
            },
            {
                id: 'NAME',
                name: UIHelper.buildTableColumn('Tên sản phẩm'),
                selector: (row) => row?.name ?? '',
                minWidth: '220px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'BRAND',
                name: UIHelper.buildTableColumn('Hãng sản xuất'),
                selector: (row) => (
                    <span className="cursor-pointer hover-opacity-60" onClick={() => {
                        Utils.openInNewTab(`${AppRoute.manageChargeBrand}/${!stationId ? row?.brandId ?? '' : row?.category?.chargeBrand?.brandId ?? ''}`)
                    }}>
                        {!stationId ? row?.chargeBrand?.name ?? '' : row?.category?.chargeBrand?.name ?? ''}
                    </span>
                ),
                minWidth: '220px',
                style: tableStyle.columns.link,
            },
            {
                id: isAdmin ? 'PRICE' : '',
                name: UIHelper.buildTableColumn('Đơn giá', 'VNĐ'),
                selector: (row) => row?.price ? Utils.formatNumber(row?.price) : '',
                minWidth: '160px',
                style: tableStyle.columns.normal,
                right: true,
            },
            {
                id: stationId ? 'QUANTITY' : '',
                name: UIHelper.buildTableColumn('Số lượng'),
                selector: (row) => row?.quantity ? Utils.formatNumber(row?.quantity) : '',
                minWidth: '160px',
                style: tableStyle.columns.normal,
                right: true,
            },
            {
                id: 'UNIT',
                name: UIHelper.buildTableColumn('Đơn vị tính'),
                selector: (row) => !stationId ? row?.unit ?? '' : row?.category?.unit ?? '',
                minWidth: '120px',
                style: tableStyle.columns.normal,
                right: true,
            },
            {
                id: 'DESCRIPTION',
                name: UIHelper.buildTableColumn('Mô tả'),
                selector: (row) => {
                    return (
                        <KTTooltip text={row?.description}>
                            <div className="my-2 text-ellipsis-2-lines" style={{ whiteSpace: 'normal' }}>
                                {row?.description}
                            </div>
                        </KTTooltip>
                    );
                },
                minWidth: '400px',
                minWidth: '260px',
                style: tableStyle.columns.normal,
            },
            {
                name: UIHelper.buildTableColumn('Thao tác'),
                maxWidth: '140px',
                style: {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#fff',
                    borderLeft: '1px solid #ebedfe',
                },
                cell: (row) => {
                    return UIHelper.buildMoreActionsButton(buildMoreActions(row), true);
                },
                omit: !isAdmin && !isMine
            },
        ];
    }, [user, isAdmin, isMine]);
    const [fullColumnIds, setFullColumnIds] = useState(
        fullColumns?.map((row) => row?.id).filter(Boolean) || []
    );
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        resetDefaultTableColumn,
        onSelectAll,
        onUnSelectAll,
        fullColumnsSorted,
        fullColumnsIdSorted,
        saveChanges,
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
    const [selectedChargeBrand, setSelectedChargeBrand] = useState(null);
    const dropdownChargeBrandEl = useMemo(() => {
        return (
            <CustomDropdown2
                id="brandId"
                name="brandId"
                toggleElement={
                    <button
                        className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
                        type="button"
                    >
                        {selectedChargeBrand?.name ?? 'Chọn hãng sản xuất'}
                    </button>
                }
                selectedValue={selectedChargeBrand}
                dropdownMenuItems={[{ name: 'Hãng sản xuất', role: 'CHARGE_BRAND' }]}
                getSubItems={async (item) => {
                    return ChargeHelper.API.requestGetChargeBrandList({
                        page: item?.page || 0,
                        limit: 10,
                        q: item?.q || '',
                    }).then((data) => {
                        return {
                            values: data.brands?.map((row) => ({
                                id: row?.brandId,
                                name: row?.name,
                                item: row,
                            })),
                            total: data.total,
                        };
                    });
                }}
                dropdownMenuStyle={{ maxHeight: '30vh' }}
                dropdownMenuClassName="overflow-auto w-100"
                onChange={(value) => {
                    setSelectedChargeBrand(value);
                    Global.g_filterChargeInventoryList = {
                        ...Global.g_filterChargeInventoryList,
                        brandId: value?.id,
                        page: 0,
                    };
                    handleRefresh();
                }}
                isDropdownForm={true}
                titleSearchable={true}
                searchable={true}
                searchFormClassName="mx-3 my-1 search-form-border"
                ignoreConfirmClick={true}
                defaultValue={{
                    name: 'Hãng sản xuất',
                    role: 'CHARGE_BRAND',
                }}
                footerEl={
                    <div
                        className="p-4 position-sticky sticky-bottom bg-white"
                        style={{
                            borderTop: `1px solid ${AppColors.gray[300]}`,
                        }}
                    >
                        {UIHelper.buildNegativeButton('Bỏ chọn', () => {
                            setSelectedChargeBrand(null);
                            Global.g_filterChargeInventoryList = {
                                ...Global.g_filterChargeInventoryList,
                                brandId: '',
                                modelId: '',
                                page: 0,
                            };
                            handleRefresh();
                        })}
                    </div>
                }
            />
        );
    }, [selectedChargeBrand]);


    // MARK: ---- METHODS ----
    const buildMoreActions = (row) => {
        const actions = [];
        if (isAdmin || isMine) {
            // chinh sua
            actions.push({
                text: 'Chỉnh sửa',
                iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
                click: () => {
                    if (!stationId) {
                        handleAddNew(row);
                    } else {
                        handleAddInventory(row);
                    }
                },
            });
            // xoa
            if (!stationId) {
                actions.push({
                    text: 'Xóa sản phẩm',
                    iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
                    click: () => {
                        handleDelete(row);
                    },
                });
            } else {
                actions.push({
                    text: 'Xóa thiết bị',
                    iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
                    click: () => {
                        handleDeleteInventory(row);
                    },
                });
            }
        }
        return actions;
    };

    const handleRefresh = () => {
        setFilter({ ...Global.g_filterChargeInventoryList });
    };
    const handleDelete = (row) => {
        ChargeHelper.UI.Inventory.showDialogConfirmDeleteCategory(row, () => {
            handleRefresh();
        });
    };
    const handleDeleteInventory = (row) => {
        ChargeHelper.UI.Inventory.showDialogConfirmDeleteInventory(row, () => {
            handleRefresh();
        });
    };
    const handleAddNew = (row) => {
        AppCustomDialogHelper.show(
            !!row ? 'Sửa sản phẩm' : 'Thêm sản phẩm',
            <DialogChargeInventoryAddView
                item={row}
                onCancel={() => AppCustomDialogHelper.hide()}
                onDone={() => {
                    AppCustomDialogHelper.hide();
                    handleRefresh();
                }}
            />,
            'lg',
            false,
            true
        );
    };
    const handleAddInventory = (row) => {
        AppCustomDialogHelper.show(
            !!row ? 'Sửa thiết bị khác' : 'Thêm thiết bị khác',
            <DialogInventoryAddView
                item={row}
                stationId={stationId}
                onCancel={() => AppCustomDialogHelper.hide()}
                onDone={() => {
                    AppCustomDialogHelper.hide();
                    handleRefresh();
                }}
            />,
            'lg',
            false,
            true
        );
    };

    return (
        <div className={`${!showFullUI ? 'px-4' : ''}`}>
            {UIHelper.buildCard(
                <div className="pb-4">
                    {/* header */}
                    {showFullUI && (
                        <div className="px-4 pt-4 pb-3 d-flex align-items-center justify-content-between">
                            {/* title */}
                            <div className="d-flex flex-column">
                                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                                    Quản lý trang thiết bị & dịch vụ
                                </span>
                                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                                    Tổng:{' '}
                                    <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                                        {Utils.formatNumber(total)} bản ghi
                                    </span>
                                </span>
                            </div>

                            {/* actions */}
                            {UIHelper.buildGreenButton(
                                'Thêm sản phẩm',
                                handleAddNew,
                                <i className="fa-regular fa-circle-plus" />
                            )}
                        </div>
                    )}

                    {/* body */}
                    <div className={`${!showFullUI ? 'pt-4' : ''}`}>
                        {/* filter */}
                        <div className="row m-0 p-0 px-2">
                            {/* tim kiem */}
                            <div className="col-3 mb-4 px-2">
                                {UIHelper.buildFormLabel('Tìm kiếm')}
                                <KTFormSearch
                                    name="search"
                                    placeholder="Theo tên sản phẩm, mã sản phẩm"
                                    value={filter.q}
                                    onSubmit={(text) => {
                                        Global.g_filterChargeInventoryList = {
                                            ...Global.g_filterChargeInventoryList,
                                            q: text,
                                            page: 0,
                                        };
                                        handleRefresh();
                                    }}
                                />
                            </div>
                            {/* loc theo hang san xuat */}
                            <div className="col-3 mb-4 px-2">
                                {UIHelper.buildFormLabel('Hãng sản xuất')}
                                {dropdownChargeBrandEl}
                            </div>
                            <div className="col-3 mb-4 px-2"></div>
                            <div className="col-3 mb-4 px-2 d-flex align-items-end justify-content-end">
                                {isAdmin && stationId && UIHelper.buildGreenButton(
                                    'Thêm thiết bị',
                                    handleAddInventory,
                                    <i className="fa-regular fa-circle-plus" />
                                )}
                            </div>
                        </div>

                        {/* table */}
                        <div className="px-4">
                            <KTTable
                                columns={columns}
                                items={chargeInventoryList}
                                selectableRows={false}
                                striped={false}
                                compactGrid={false}
                                hasActionColumn={isAdmin || isMine}
                                onClickedRow={(row) => { }}
                                onSelectedRowsChanged={(rows) => { }}
                                onDoubleClickedRow={(row) => { }}
                                noDataComponent={UIHelper.buildEmptyNoData(
                                    'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                                )}
                            />
                        </div>

                        {/* pagination */}
                        <div className="px-4 mt-2">
                            <KTPagination
                                currentPage={filter.page + 1}
                                rowCount={total}
                                rowsPerPage={filter.limit}
                                currentItemInPageCount={chargeInventoryList?.length}
                                onChangePage={(newPage) => {
                                    const iNewPage = parseInt(newPage);
                                    Global.g_filterChargeInventoryList = {
                                        ...Global.g_filterChargeInventoryList,
                                        page: iNewPage - 1,
                                    };
                                    handleRefresh();
                                }}
                                onChangeRowsPerPage={(newRowsPerPage) => {
                                    const iNewRowsPerPage = parseInt(newRowsPerPage);
                                    Global.g_filterChargeInventoryList = {
                                        ...Global.g_filterChargeInventoryList,
                                        limit: iNewRowsPerPage,
                                        page: 0,
                                    };
                                    handleRefresh();
                                }}
                                loading={false}
                                color={KTPaginationColor.success}
                                appendText="bản ghi"
                            />
                        </div>
                    </div>
                </div>,
                false,
                false,
                true,
                false,
                AppColors.gray[300],
                !showFullUI
            )}
        </div>
    );
}

export default ChargeInventoryListView;
